import type { Prng } from './rng.js';
import type { CombatResult, TerritoryId } from './types.js';

export interface ResolveCombatInput {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly attackerUnits: number;
  readonly defenderUnits: number;
  readonly attackingDice: 1 | 2 | 3;
}

/** Defender always rolls min(2, defenderUnits). */
export function defenderDiceFor(defenderUnits: number): 1 | 2 {
  return defenderUnits >= 2 ? 2 : 1;
}

/**
 * Resolves a single attack roll. Pure: no state mutation, all randomness
 * sourced from the supplied Prng. Caller is responsible for validating
 * legality (adjacency, ownership, phase) and for applying the resulting
 * losses to GameState.
 *
 * Combat rules: attacker chooses 1–3 dice (capped at attackerUnits-1),
 * defender rolls min(2, defenderUnits), highest dice are paired in
 * descending order, defender wins ties.
 */
export function resolveCombat(input: ResolveCombatInput, prng: Prng): CombatResult {
  const { from, to, attackerUnits, defenderUnits, attackingDice } = input;

  if (attackerUnits < 2) {
    throw new Error(
      `resolveCombat: attacker must have at least 2 units, got ${attackerUnits}`
    );
  }
  if (defenderUnits < 1) {
    throw new Error(
      `resolveCombat: defender must have at least 1 unit, got ${defenderUnits}`
    );
  }
  if (attackingDice < 1 || attackingDice > 3) {
    throw new Error(
      `resolveCombat: attackingDice must be 1, 2, or 3, got ${attackingDice}`
    );
  }
  if (attackingDice > attackerUnits - 1) {
    throw new Error(
      `resolveCombat: attackingDice ${attackingDice} exceeds attackerUnits-1 (${attackerUnits - 1})`
    );
  }

  const defenderDice = defenderDiceFor(defenderUnits);

  const attackerRolls: number[] = [];
  for (let i = 0; i < attackingDice; i++) attackerRolls.push(prng.nextDie());

  const defenderRolls: number[] = [];
  for (let i = 0; i < defenderDice; i++) defenderRolls.push(prng.nextDie());

  const aSorted = [...attackerRolls].sort((a, b) => b - a);
  const dSorted = [...defenderRolls].sort((a, b) => b - a);

  const pairs = Math.min(attackingDice, defenderDice);
  let attackerLosses = 0;
  let defenderLosses = 0;
  for (let i = 0; i < pairs; i++) {
    const a = aSorted[i]!;
    const d = dSorted[i]!;
    if (a > d) defenderLosses++;
    else attackerLosses++; // ties go to defender
  }

  return {
    from,
    to,
    roll: { attackerDice: attackerRolls, defenderDice: defenderRolls },
    attackerLosses,
    defenderLosses,
    conquered: defenderUnits - defenderLosses === 0,
  };
}
